import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

interface Post {
  id: string;
  category: string;
  categoryName: string;
  title: string;
  content: string;
  author: string;
  authorAvatar: string;
  createdAt: string;
  views: number;
  comments: number;
  likes: number;
  isHot: boolean;
  image: string | null;
}

interface PostCardProps {
  post: Post;
  index: number;
  onLike: (id: string) => void;
  likedPosts: Set<string>;
}

const categoryColors: Record<string, string> = {
  free: 'bg-neon-purple/15 text-neon-purple',
  tips: 'bg-neon-green/15 text-neon-green',
  review: 'bg-neon-pink/15 text-neon-pink',
  qna: 'bg-yellow-500/15 text-yellow-400',
  news: 'bg-neon-blue/15 text-neon-blue',
};

export default function PostCard({ post, index, onLike, likedPosts }: PostCardProps) {
  const isLiked = likedPosts.has(post.id);
  const navigate = useNavigate();

  const timeAgo = () => {
    const posted = new Date(post.createdAt);
    const now = new Date();
    const diffH = Math.floor((now.getTime() - posted.getTime()) / (1000 * 60 * 60));
    if (diffH < 1) return '방금 전';
    if (diffH < 24) return `${diffH}시간 전`;
    const diffD = Math.floor(diffH / 24);
    if (diffD === 1) return '어제';
    return `${diffD}일 전`;
  };

  const handleCardClick = () => {
    navigate(`/community/${post.id}`);
  };

  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      className="group cursor-pointer"
      onClick={handleCardClick}
    >
      <div className="bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 hover:border-neon-purple/30 transition-all duration-300 hover:-translate-y-1 hover:shadow-glow overflow-hidden">
        {/* Image */}
        {post.image && (
          <div className="relative w-full h-48 overflow-hidden">
            <img
              src={post.image}
              alt={post.title}
              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-dark-deep/70 via-transparent to-transparent"></div>
            {post.isHot && (
              <span className="absolute top-3 left-3 bg-red-500/90 text-white text-[10px] font-bold px-2.5 py-1 rounded-md flex items-center gap-1">
                <i className="ri-fire-fill w-3 h-3 flex items-center justify-center"></i>
                HOT
              </span>
            )}
          </div>
        )}

        {/* Content */}
        <div className="p-5">
          {/* Top */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className={`text-[10px] font-semibold px-2.5 py-1 rounded-md ${categoryColors[post.category] || 'bg-white/10 text-gray-400'}`}>
                {post.categoryName}
              </span>
              {!post.image && post.isHot && (
                <span className="bg-red-500/90 text-white text-[10px] font-bold px-2.5 py-1 rounded-md flex items-center gap-1">
                  <i className="ri-fire-fill w-3 h-3 flex items-center justify-center"></i>
                  HOT
                </span>
              )}
            </div>
            <span className="text-[11px] text-gray-600">{timeAgo()}</span>
          </div>

          {/* Title */}
          <h3 className="text-base font-bold text-white mb-2 group-hover:text-neon-purple transition-colors line-clamp-2">
            {post.title}
          </h3>

          {/* Preview */}
          <p className="text-sm text-gray-500 mb-4 line-clamp-2 leading-relaxed">
            {post.content}
          </p>

          {/* Bottom */}
          <div className="flex items-center justify-between pt-3 border-t border-white/5">
            {/* Author */}
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full overflow-hidden">
                <img src={post.authorAvatar} alt={post.author} className="w-full h-full object-cover" />
              </div>
              <span className="text-xs text-gray-400">{post.author}</span>
            </div>

            {/* Stats */}
            <div className="flex items-center gap-3 text-[11px] text-gray-500">
              <span className="flex items-center gap-1">
                <i className="ri-eye-line w-3.5 h-3.5 flex items-center justify-center"></i>
                {post.views.toLocaleString()}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-chat-3-line w-3.5 h-3.5 flex items-center justify-center"></i>
                {post.comments}
              </span>
              <button
                onClick={(e) => { e.stopPropagation(); onLike(post.id); }}
                className={`flex items-center gap-1 transition-colors cursor-pointer ${
                  isLiked ? 'text-neon-pink' : 'hover:text-neon-pink'
                }`}
              >
                <i className={`${isLiked ? 'ri-heart-fill' : 'ri-heart-line'} w-3.5 h-3.5 flex items-center justify-center`}></i>
                {post.likes + (isLiked ? 1 : 0)}
              </button>
            </div>
          </div>
        </div>
      </div>
    </motion.article>
  );
}
